"use client";

import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { getOddsLiveClient } from "@/lib/odds-screen/live/client";
import { bestPriceRowIds } from "@/lib/odds-screen/live/derive";
import type { Board, BoardTopic, BookId, FamilyId, LeagueId, MarketSpec, RowId } from "@/lib/odds-screen/live/types";

const NONE: ReadonlySet<RowId> = new Set();

function sameIds(a: ReadonlySet<RowId>, b: ReadonlySet<RowId>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

export function useBestPriceRows(
  league: LeagueId,
  family: FamilyId,
  specs: readonly MarketSpec[],
  selected: ReadonlySet<BookId>,
  minStake: number,
  enabled: boolean,
): ReadonlySet<RowId> | null {
  const client = getOddsLiveClient();
  const topic = useMemo<BoardTopic>(() => ({ kind: "board", league, family }), [league, family]);
  const cache = useRef<{ board: Board | null; specs: readonly MarketSpec[]; selected: ReadonlySet<BookId>; minStake: number; ids: ReadonlySet<RowId> } | null>(null);

  const listen = useCallback(
    (onChange: () => void) => (enabled ? client.listen(topic, onChange) : () => {}),
    [client, topic, enabled],
  );
  const read = (): ReadonlySet<RowId> | null => {
    if (!enabled) return null;
    const board = client.read(topic).value;
    if (board === null) return NONE;
    const held = cache.current;
    if (held && held.board === board && held.specs === specs && held.selected === selected && held.minStake === minStake) return held.ids;
    const next = bestPriceRowIds(board, specs, selected, minStake);
    const ids = held && sameIds(held.ids, next) ? held.ids : next;
    cache.current = { board, specs, selected, minStake, ids };
    return ids;
  };
  return useSyncExternalStore(listen, read, read);
}
